import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import ToolSeoHero from "../components/tools-seo/ToolSeoHero";
import ToolSeoBenefits from "../components/tools-seo/ToolSeoBenefits";
import ToolSeoFAQ from "../components/tools-seo/ToolSeoFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, { buildFAQSchema } from "../components/seo/StructuredData";

const THEME_KEY = "hookora_theme";

const thumbnailFaqs = [
  {
    question: "What is a thumbnail text analyzer?",
    answer:
      "A thumbnail text analyzer is a tool that helps review and improve thumbnail wording so it feels clearer, sharper, and more effective for content packaging."
  },
  {
    question: "Who is Hookora’s Thumbnail Text Analyzer for?",
    answer:
      "It is built for creators, YouTubers, marketers, and anyone who wants stronger thumbnail wording for videos or visual content."
  },
  {
    question: "Why does thumbnail text matter?",
    answer:
      "Thumbnail text affects first impression. It can make your packaging easier to understand and more attention-friendly before someone clicks."
  },
  {
    question: "Can this help with YouTube content?",
    answer:
      "Yes. This tool is especially useful for YouTube creators, but the principles can also support broader content packaging decisions."
  },
  {
    question: "Does Hookora only analyze thumbnail text?",
    answer:
      "No. Hookora also includes tools for hooks, titles, captions, hashtags, descriptions, and short scripts."
  },
  {
    question: "Is this useful if I already have a title?",
    answer:
      "Yes. In fact, it is often more useful because thumbnail text works best when it complements your title and overall content angle."
  }
];

function ThumbnailTextAnalyzerPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const pageStyle = useMemo(
    () => ({
      background: isDark ? "#020617" : "#f8fafc",
      backgroundImage: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      minHeight: "100vh"
    }),
    [isDark]
  );

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Thumbnail Text Analyzer | Hookora"
        description="Use Hookora’s Thumbnail Text Analyzer to improve thumbnail wording for clarity, stronger first impression, and better click-first content packaging."
        path="/thumbnail-text-analyzer"
      />
      <StructuredData
        id="thumbnail-text-analyzer-faq-schema"
        data={buildFAQSchema(thumbnailFaqs)}
      />

      <Navbar />
      <ToolSeoHero
        badge="Hookora Tool • Thumbnail Text Analyzer"
        title="Thumbnail Text Analyzer for creators who want stronger click-first packaging"
        description="The Hookora Thumbnail Text Analyzer helps creators improve thumbnail wording so it feels clearer, sharper, and more attention-friendly. Instead of guessing whether your text works, you get stronger direction for making thumbnail words more effective."
        highlights={[
          "Improve clarity",
          "Increase punch",
          "Support click potential",
          "Built for creator workflow"
        ]}
        primaryCtaLabel="Start Free"
        primaryCtaTo="/register"
        secondaryCtaLabel="See Features"
        secondaryCtaTo="/features"
        previewTitle="What the Thumbnail Text Analyzer helps you do"
        previewItems={[
          {
            label: "Clarity",
            text: "Spot thumbnail wording that feels weak, vague, or harder to understand quickly."
          },
          {
            label: "Impact",
            text: "Improve the punch and tension of your text so it feels more attention-worthy."
          },
          {
            label: "Packaging",
            text: "Make your thumbnail wording work better with your title, hook, and content angle."
          }
        ]}
      />

      <ToolSeoBenefits
        eyebrow="Why creators use it"
        title="A thumbnail text analyzer should help your content look stronger before anyone even clicks"
        description="Thumbnail text matters because it shapes first impression and click potential. Hookora helps creators refine thumbnail wording so it feels stronger, cleaner, and easier to pair with the rest of their content packaging."
        benefits={[
          {
            title: "Improve weak thumbnail wording faster",
            text: "Instead of guessing whether your thumbnail text works, Hookora helps you move toward clearer and stronger wording faster."
          },
          {
            title: "Make your message easier to read at a glance",
            text: "Thumbnail text works best when people understand it quickly. Hookora helps improve that first-read clarity."
          },
          {
            title: "Support stronger click-first presentation",
            text: "Your thumbnail text is part of the packaging that influences whether someone pays attention. Better wording makes that packaging stronger."
          },
          {
            title: "Reduce friction in your publishing workflow",
            text: "When you create often, thumbnail wording can become repetitive or rushed. Hookora helps make that step easier."
          },
          {
            title: "Connect thumbnail text with the rest of your content",
            text: "Your thumbnail text works better when it matches your title, hook, and message. Hookora helps that alignment feel cleaner."
          },
          {
            title: "Useful for creators trying to improve first impressions",
            text: "Whether you are posting regularly or testing better packaging, stronger thumbnail wording helps your content feel more intentional."
          }
        ]}
      />

      <ToolSeoFAQ
        title="Thumbnail Text Analyzer FAQ"
        intro="Here are a few common questions about Hookora’s Thumbnail Text Analyzer."
        faqs={thumbnailFaqs}
      />

      <ToolSeoCTA
        badge="Start free with Hookora"
        title="Use Hookora’s Thumbnail Text Analyzer to improve first impression and click potential"
        description="If you want sharper thumbnail wording, better content packaging, and a smoother publishing workflow, Hookora gives you a practical place to start."
        primaryLabel="Create Free Account"
        primaryTo="/register"
        secondaryLabel="Explore Features"
        secondaryTo="/features"
      />
    </div>
  );
}

export default ThumbnailTextAnalyzerPage;